import { AbstractVpaDataTableReport } from '../../report/abstract-vpa-data-table-report';
import ActiveFiltersInterface from '@vertical-plus/vue-js-components/lib/model/vos/search/active-filters-interface';
import Client from '@/model/entity/client/client';
import ColumnInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/column-interface';
import FilterInterface from '@vertical-plus/vue-js-components/lib/model/vos/search/filter-interface';
import ServiceContainer from '../../service-container';
import SortByInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/sort-by-interface';
import TechspecSearchConditionsInterface from '@/model/vo/job/techspec-search-conditions-interface';
import TechspecSearchResultsVo from '@/model/vo/job/techspec-search-results-vo';
import WidgetInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/widget-interface';

export default class TechspecDataTableReport extends AbstractVpaDataTableReport
{
    public id = 'techspecs';

    public get widgets(): WidgetInterface[]
    {
        return [];
    }

    public get filters(): FilterInterface[]
    {
        return [
            {
                key: 'id',
                type: 'string',
                title: this.i18n('general.field.id'),
            },
            {
                key: 'client',
                type: 'string',
                title: this.i18n('general.field.client'),
                optionsCallback: async(search: string) => this.serviceContainer.clientService.clientFormOptionsCallback(search),
                titlesCallback: async(values: string[]) => this.serviceContainer.clientService.clientFormTitlesCallback(values),
            },
            {
                key: 'manager',
                type: 'string',
                title: this.i18n('general.field.account_manager'),
                optionsCallback: async(search: string) => this.serviceContainer.userService.formOptionsCallback(search),
                titlesCallback: async(values: string[]) => this.serviceContainer.userService.formTitlesCallback(values),
            },
            {
                key: 'type',
                type: 'string',
                title: this.i18n('general.field.type'),
                optionsCallback: async(search: string) => this.serviceContainer.techspecService.techspecTypeFormOptionsCallback(search),
                titlesCallback: async(values: string[]) => this.serviceContainer.techspecService.techspecTypeFormTitlesCallback(values),
            },
            {
                key: 'name',
                type: 'string',
                title: this.i18n('general.field.name'),
            },
            {
                key: 'active',
                type: 'boolean',
                title: this.i18n('general.field.active'),
            },
        ];
    }

    public get defaultFilters(): ActiveFiltersInterface
    {
        return {
            active: true,
        };
    }

    public get columns(): ColumnInterface[]
    {
        const columns: ColumnInterface[] = [
            {
                key: 'id',
                title: this.i18n('general.field.id'),
                width: 100,
            },
            {
                key: 'name',
                title: this.i18n('general.field.name'),
            },
            {
                key: 'type.title',
                title: this.i18n('general.field.type'),
                width: 150,
            },
        ];

        if (!this.client)
        {
            columns.push({
                key: 'client.name',
                title: this.i18n('general.field.client'),
                width: 200,
            });
        }

        columns.push(...[
            {
                key: 'manager.fullName',
                title: this.i18n('general.field.manager'),
                width: 150,
            },
            {
                key: 'active',
                title: this.i18n('general.field.active'),
                width: 80,
                align: 'center',
            },
            {
                key: 'dateCreated',
                title: this.i18n('general.field.date_created'),
                width: 150,
            },
            {
                key: 'options',
                title: this.i18n('general.field.options'),
                width: 80,
                align: 'end',
            },
        ] as ColumnInterface[]);

        return columns;
    }

    public get defaultColumns(): string[]
    {
        if (this.savedDefaultColumns)
        {
            return this.savedDefaultColumns;
        }

        return [
            'id',
            'name',
            'type.title',
            'client.name',
            'manager.fullName',
            'dateCreated',
            'options',
        ];
    }

    public get sortBys(): SortByInterface[]
    {
        return [
            {
                key: 'id',
                title: this.i18n('general.field.id'),
                order:
                [
                    {
                        key: 'id',
                        descending: false,
                    },
                ],
            },
            {
                key: 'name',
                title: this.i18n('general.field.name'),
                order:
                [
                    {
                        key: 'name',
                        descending: false,
                    },
                ],
            },
            {
                key: 'dateCreated',
                title: this.i18n('general.field.date_created'),
                order:
                [
                    {
                        key: 'dateCreated',
                        descending: true,
                    },
                ],
            },
            {
                key: 'client.name',
                title: this.i18n('general.field.client'),
                order:
                [
                    {
                        key: 'client.name',
                        descending: false,
                    },
                ],
            },
            {
                key: 'manager.fullName',
                title: this.i18n('general.field.manager'),
                order:
                [
                    {
                        key: 'manager.fullName',
                        descending: false,
                    },
                ],
            },
        ];
    }

    public defaultSortBy = 'dateCreated';

    private client: Client|null = null;
    private results: TechspecSearchResultsVo|null = null;

    public constructor(serviceContainer: ServiceContainer, client: Client|null = null)
    {
        super(serviceContainer);

        this.client = client;
    }

    public setClient(client: Client|null)
    {
        this.client = client;
    }

    public async loadResults(conditions: TechspecSearchConditionsInterface): Promise<TechspecSearchResultsVo>
    {
        // Force client filter if a client has been defined
        if (this.client?.id)
        {
            conditions.client = `${ this.client.id }`;
        }

        // Fix name order
        if (conditions.order?.length === 1 && conditions.order[0].key === 'manager.fullName')
        {
            conditions.order = [
                {
                    key: 'manager.firstName',
                    descending: conditions.order[0].descending,
                },
            ];
        }

        // Force NOT meetings
        conditions.meeting = false;

        const results = await this.serviceContainer.techspecService.findTechspecs(conditions);

        this.results = Object.assign({}, results);

        return results;
    }
}